import React, { useEffect, useMemo } from 'react';
import NumberFormat from 'react-number-format';
import cloneDeep from 'lodash/cloneDeep';
import { Spring } from 'react-spring/renderprops';

import PageLayout from 'layouts/PageLayout';
import Sidebar from 'scenes/Sidebar';
import ListSearch from 'components/ListSearch';
import BasicButton from 'components/BasicButton';
import InboxTabs from 'scenes/Inbox/components/InboxTabs';
import List from 'scenes/List';
import InboxItem from 'scenes/List/components/InboxItem';
import InBounds from 'scenes/Sidebar/components/InBounds';
import ListSearchContentLoader from 'components/ListSearchContentLoader';
import FiltersContentLoader from 'components/FiltersContentLoader';
import GridHistory from 'components/GridHistory';
import UpgradeRecruiterAccount from 'scenes/Dialog/components/UpgradeRecruiterAccount';
import HorizontalLoader from 'components/HorizontalLoader';

import { useApi } from 'hooks/useApi';
import { useGrid } from 'hooks/useGrid';
import { updateHistory } from 'helpers/grid-history';
import { Role } from 'helpers/user';
import { MESSAGE_FOLDER } from 'constants/message-types';
import { ENDPOINT } from 'constants/endpoints';
import { usePrevious } from 'hooks/usePrevious';

import { action } from './actions';

import './styles.css';

const Inbox = props => {
  const {
    user,
    history,
    match: {
      params: { message = null },
    },
    location,
    globalActions: { updateInboxCount, updateUserInboundsCount },
  } = props;

  const api = useApi();

  const grid = useGrid(ENDPOINT.COLLECTION.INBOX, params => {
    updateHistory(history, params);
  });

  const [isCandidate, isRecruiter, isRecruiterDefault] = useMemo(
    () => [
      Role.isCandidate(user),
      Role.isRecruiter(user),
      Role.isRecruiterDefault(user),
    ],
    [user]
  );

  useEffect(() => {
    if (typeof grid.data.unreadCount !== 'undefined') {
      updateInboxCount(grid.data.unreadCount);
    }
  }, [grid.data.unreadCount]);

  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const reply = params.get('reply');

      if (Number(reply)) {
        history.replace(location.pathname);
        action.inbox.reply(
          api,
          { message: { MessageID: reply, ToRead: 0 } },
          { grid }
        );
      }
    }
  }, [location.search]);

  const handleSwitchTab = folder => {
    const nextParams = cloneDeep(grid.data.params);
    if (nextParams.page || nextParams.page === 0) {
      delete nextParams.page;
    }
    if (nextParams.search && nextParams.search === '') {
      delete nextParams.search;
    }
    if (nextParams.message) {
      delete nextParams.message;
    }
    nextParams.folder = folder;
    folder === 1 ? (nextParams.sortBy = 'all') : delete nextParams.sortBy;

    grid.actions
      .fetch(nextParams)
      .then(params => updateHistory(history, params));
  };

  const hasAccess = (showMessage = true) => {
    if (isRecruiterDefault && !isCandidate) {
      showMessage && UpgradeRecruiterAccount();
      return false;
    }
    return isRecruiter || Role.isCandidate(user, showMessage);
  };

  const handleMarkAsRead = (message, isOpen = true) => {
    if (hasAccess(false)) {
      action.inbox.markAsRead(api, { message, isOpen }, { grid });
    }
  };

  const onDelete = message => {
    if (hasAccess()) {
      action.inbox.remove(api, { message }, { grid });
    }
  };

  const onUndelete = message => {
    if (hasAccess()) {
      action.inbox.restore(api, { message }, { grid });
    }
  };

  const onDeletePermanently = message => {
    if (hasAccess()) {
      action.inbox.removePermanently(api, { message }, { grid });
    }
  };

  const onCreateMessage = () => {
    if (hasAccess()) {
      action.inbox.createMessage(grid);
    }
  };

  const onReply = message => {
    if (hasAccess()) {
      action.inbox.reply(api, { message }, { grid });
    }
  };

  const onForward = message => {
    if (hasAccess()) {
      action.inbox.forward(api, { message }, { grid });
    }
  };

  const onResumeAccept = message => {
    if (hasAccess()) {
      action.inbox.resumeAccept(api, { message }, { grid });
    }
  };

  const onResumeReject = message => {
    if (hasAccess()) {
      action.inbox.resumeReject(api, { message }, { grid });
    }
  };

  const onConnectAccept = message => {
    if (hasAccess()) {
      action.inbox.connectAccept(api, { message }, { grid });
    }
  };

  const onConnectReject = message => {
    if (hasAccess()) {
      action.inbox.connectReject(api, { message }, { grid });
    }
  };

  const itemFooterActions = {
    Inbox: [
      {
        caption: 'Reply',
        icon: 'reply',
        handler: onReply,
      },
      {
        caption: 'Delete',
        icon: 'delete',
        handler: onDelete,
      },
      {
        caption: 'Forward',
        icon: 'forward',
        handler: onForward,
      },
    ],
    Sent: [
      {
        caption: 'Forward',
        icon: 'forward',
        handler: onForward,
      },
      {
        caption: 'Delete',
        icon: 'delete',
        handler: onDelete,
      },
    ],
    Trash: [
      {
        caption: 'Restore',
        icon: 'reply',
        handler: onUndelete,
      },
      {
        caption: 'Delete',
        icon: 'delete',
        handler: onDeletePermanently,
      },
    ],
    Connect: [
      {
        caption: 'Accept',
        icon: 'accept',
        handler: onConnectAccept,
      },
      {
        caption: 'Reject',
        icon: 'reject',
        handler: onConnectReject,
      },
    ],
    Resume: [
      {
        caption: 'Accept',
        icon: 'accept',
        handler: onResumeAccept,
      },
      {
        caption: 'Reject',
        icon: 'reject',
        handler: onResumeReject,
      },
    ],
  };

  const inboxTabsActions = [
    {
      caption: `INBOX ${
        grid.data.folders && Number(grid.data.folders[0].unreadMessageCount) > 0
          ? `(${grid.data.folders[0].unreadMessageCount})`
          : ''
      }`,
      icon: 'inbox',
      selected: Number(grid.data.params.folder) === MESSAGE_FOLDER.INBOX,
      handler: () => handleSwitchTab(MESSAGE_FOLDER.INBOX),
    },
    {
      caption: `INVITATIONS ${
        grid.data.folders && Number(grid.data.folders[1].unreadMessageCount) > 0
          ? `(${grid.data.folders[1].unreadMessageCount})`
          : ''
      }`,
      icon: 'invitations',
      selected: Number(grid.data.params.folder) === MESSAGE_FOLDER.INVITATIONS,
      handler: () => handleSwitchTab(MESSAGE_FOLDER.INVITATIONS),
    },
    {
      caption: 'SENT',
      icon: 'sent',
      selected: Number(grid.data.params.folder) === MESSAGE_FOLDER.SENT,
      handler: () => handleSwitchTab(MESSAGE_FOLDER.SENT),
    },
    {
      caption: `TRASH ${
        grid.data.folders && Number(grid.data.folders[3].unreadMessageCount) > 0
          ? `(${grid.data.folders[3].unreadMessageCount})`
          : ''
      }`,
      icon: 'delete',
      selected: Number(grid.data.params.folder) === MESSAGE_FOLDER.TRASH,
      handler: () => handleSwitchTab(MESSAGE_FOLDER.TRASH),
    },
  ];

  const sortControls =
    Number(grid.data.params.folder) === MESSAGE_FOLDER.INBOX
      ? [
          {
            defaultCaption: 'All messages',
            toggledCaption: 'All messages',
            value: 'all',
          },
          {
            defaultCaption: 'Unread messages',
            toggledCaption: 'Unread messages',
            value: 'unread',
          },
        ]
      : null;

  const title = ['Inbox', 'Sent', 'Invitations', 'Trash'][
    grid.data.params.folder ? grid.data.params.folder - 1 : 0
  ];

  const gridRowsCountRef = usePrevious(Number(grid.data.rowsCount) || 0);

  return (
    <GridHistory
      history={history}
      grid={grid}
      initialParams={{
        folder: 1,
        sortBy: 'all',
      }}
      matchParams={{ message }}
    >
      <PageLayout
        pageClass="inbox-page"
        pageTitle="Inbox"
        mainContent={
          <>
            {grid.data.init ? (
              <ListSearch
                sortBy={grid.data.params.sortBy || ''}
                sortByLabel="View:"
                sortControls={sortControls}
                recordsCount={grid.data.unreadCount}
                recordsName={
                  Number(grid.data.unreadCount) === 1
                    ? 'unread message'
                    : 'unread messages'
                }
                noResultsPlaceholder=""
                search={grid.data.params.search || ''}
                searchPlaceholder="Search Message"
                onChangeSortByHandler={grid.actions.sortBy}
                onSendSearchQueryHandler={grid.actions.search}
                renderHeader={
                  <div className="inbox-page__header">
                    <h2 className="inbox-page__title">
                      {title}
                      <span className="inbox-page__counter">
                        (
                        <Spring
                          from={{ value: gridRowsCountRef }}
                          to={{ value: Number(grid.data.rowsCount) }}
                        >
                          {props => (
                            <NumberFormat
                              thousandSeparator
                              value={props.value.toFixed(0)}
                              displayType={'text'}
                            />
                          )}
                        </Spring>
                        )
                      </span>
                    </h2>
                    <div className="inbox-page__action">
                      <BasicButton
                        controlFunc={onCreateMessage}
                        btnWidth={200}
                        btnHeight={45}
                        btnFontWeight={600}
                        btnText="Compose New Message"
                        btnArrow={true}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                      />
                    </div>
                  </div>
                }
                renderTabs={<InboxTabs actions={inboxTabsActions} />}
              />
            ) : (
              <ListSearchContentLoader />
            )}

            <div className="horizontal-loader__container">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>

            {grid.data.init && (
              <List
                items={grid.data.rows}
                itemRender={(item, actions) => (
                  <InboxItem
                    key={item.MessageID}
                    user={user}
                    item={item}
                    actions={actions}
                    footerActions={itemFooterActions}
                  />
                )}
                actions={{
                  onChangePage: grid.actions.readPage,
                  onReply,
                  onDelete,
                  onRead: handleMarkAsRead,
                }}
                pagination={{
                  page: grid.data.page,
                  count: grid.data.rowsCount,
                  perPage: grid.data.perPage,
                }}
              />
            )}
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {isCandidate && (
                <InBounds
                  inboundsCount={user.inbounds}
                  onInboundsUpdate={updateUserInboundsCount}
                />
              )}
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistory>
  );
};

export default Inbox;
